import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '@/shared/ui/Card';
import { Icon } from '@/shared/ui/Icon';
import { Button } from '@/shared/ui/Button';
import { LoadingSpinner } from '@/shared/ui/LoadingSpinner';
import { approvePayment, failPayment } from '@/shared/api/payment';
import { formatKRW } from '@/shared/lib/money';

type ResultState =
  | { phase: 'processing' }
  | { phase: 'approved'; amount: number }
  | { phase: 'canceled' }
  | { phase: 'failed' };

const RETURN_KEY = 'fp-pay-return';

/**
 * 카카오페이 결제창 복귀 페이지.
 * approval_url → ?paymentId=...&pg_token=... / cancel·fail → &outcome=cancel|fail
 */
export default function PayKakaoResultPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState<ResultState>({ phase: 'processing' });
  const startedRef = useRef(false);

  const returnPath = sessionStorage.getItem(RETURN_KEY) ?? '/';

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const paymentId = params.get('paymentId');
    const pgToken = params.get('pg_token');
    const outcome = params.get('outcome');

    if (!paymentId) {
      setState({ phase: 'failed' });
      return;
    }
    if (outcome === 'cancel' || outcome === 'fail') {
      void failPayment(paymentId, outcome === 'cancel')
        .finally(() => setState({ phase: outcome === 'cancel' ? 'canceled' : 'failed' }));
      return;
    }
    if (!pgToken) {
      setState({ phase: 'failed' });
      return;
    }
    approvePayment(paymentId, pgToken)
      .then((res) => setState({ phase: 'approved', amount: res.amount }))
      .catch(() => setState({ phase: 'failed' }));
  }, [params]);

  return (
    <section className="min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-sm !p-8 text-center">
        {state.phase === 'processing' && (
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner label="결제 승인 중…" />
            <p className="text-body text-ink-muted">카카오페이 결제를 확인하고 있어요.</p>
          </div>
        )}

        {state.phase === 'approved' && (
          <div className="space-y-3">
            <span
              aria-hidden="true"
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-success"
            >
              <Icon name="check" size={24} />
            </span>
            <h1 className="text-feature font-semibold text-ink">송금 완료</h1>
            <p className="text-body text-ink-muted">
              {formatKRW(state.amount)}을 카카오페이로 보냈어요.
              <br />
              정산 내역에 자동 반영됐습니다.
            </p>
          </div>
        )}

        {state.phase === 'canceled' && (
          <div className="space-y-3">
            <span
              aria-hidden="true"
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-surface-muted text-ink-muted"
            >
              <Icon name="back" size={24} />
            </span>
            <h1 className="text-feature font-semibold text-ink">결제 취소</h1>
            <p className="text-body text-ink-muted">결제를 취소했어요. 언제든 다시 시도할 수 있어요.</p>
          </div>
        )}

        {state.phase === 'failed' && (
          <div className="space-y-3">
            <span
              aria-hidden="true"
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-error-soft text-error"
            >
              <Icon name="x" size={24} />
            </span>
            <h1 className="text-feature font-semibold text-ink">결제 실패</h1>
            <p className="text-body text-ink-muted">결제가 완료되지 않았어요. 다시 시도해주세요.</p>
          </div>
        )}

        {state.phase !== 'processing' && (
          <div className="mt-6">
            <Link to={returnPath}>
              <Button variant="brand" className="w-full">정산으로 돌아가기</Button>
            </Link>
          </div>
        )}
      </Card>
    </section>
  );
}
